import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, Chip,
  IconButton, TextField, MenuItem, Tooltip, LinearProgress,
  Divider, Paper
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon,
  Flag as MilestoneIcon,
  ArrowBack as ArrowBackIcon, CheckCircle as CheckCircleIcon, Event as EventIcon,
  Folder as ProjectIcon
} from '@mui/icons-material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { projectApi } from '../services/projectApi';
import { PrjMilestone, MilestoneStatus, PrjProject } from '../types/project';

const emptyMilestone = (): Partial<PrjMilestone> => ({
  projectId: undefined,
  title: '',
  date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  status: 'PENDING',
  description: '',
});

const getStatusColor = (status: MilestoneStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'PENDING': return 'info';
    case 'REACHED': return 'success';
    case 'DELAYED': return 'error';
    default: return 'default';
  }
};

const getStatusLabel = (status: MilestoneStatus, t: (key: string, fallback?: string) => string): string => {
  switch (status) {
    case 'PENDING': return t('project.milestones.statusPending', 'En attente');
    case 'REACHED': return t('project.milestones.statusReached', 'Atteint');
    case 'DELAYED': return t('project.milestones.statusDelayed', 'En retard');
    default: return status;
  }
};

export const MilestonesPage: React.FC = () => {
  const { t } = useLanguage();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [milestonesList, setMilestonesList] = useState<PrjMilestone[]>([]);
  const [projectsList, setProjectsList] = useState<PrjProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<PrjMilestone>>(emptyMilestone());
  const [isEditing, setIsEditing] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [milestones, projects] = await Promise.all([
        projectApi.getMilestones(),
        projectApi.getProjects()
      ]);
      setMilestonesList(milestones);
      setProjectsList(projects);
    } catch (err) {
      console.error('Error loading milestones:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openNew = () => {
    setFormData(emptyMilestone());
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (milestone: PrjMilestone) => {
    setFormData({ ...milestone });
    setIsEditing(true);
    setView('form');
  };

  const backToList = () => {
    setView('list');
  };

  const handleDelete = async (milestone: PrjMilestone) => {
    const milestoneId = (milestone.id || milestone.PML_IDF)!;
    const confirmed = await blobticMessage.confirm({
      message: t('project.milestones.confirmDelete', 'Êtes-vous sûr de vouloir supprimer ce jalon ?'),
      title: t('common.confirm', 'Confirmation')
    });

    if (confirmed) {
      const res = await projectApi.deleteMilestone(milestoneId);
      if (res.success) {
        blobticMessage.success(t('project.milestones.deleteSuccess', 'Jalon supprimé avec succès.'));
        loadData();
      } else {
        blobticMessage.error(res.message || t('common.error', 'Erreur lors de la suppression.'));
      }
    }
  };

  const handleToggleReached = async (milestone: PrjMilestone) => {
    const milestoneId = (milestone.id || milestone.PML_IDF)!;
    const nextStatus: MilestoneStatus = milestone.status === 'REACHED' ? 'PENDING' : 'REACHED';
    const res = await projectApi.updateMilestone({
      PML_IDF: milestoneId,
      PML_STATUS: nextStatus,
    });
    if (res.success) {
      loadData();
    }
  };

  const handleValidate = (): string | null => {
    if (!formData.title?.trim()) {
      return t('project.milestones.titleRequired', 'Le titre du jalon est requis.');
    }
    if (!formData.projectId && !formData.PRJ_IDF) {
      return t('project.milestones.projectRequired', 'Veuillez sélectionner un projet associé.');
    }
    return null;
  };

  const handleDataChange = () => {
    backToList();
    loadData();
  };

  if (view === 'form') {
    const formPayload = {
      ...formData,
      PML_IDF: formData.id || formData.PML_IDF,
      PML_CODE: formData.code || formData.PML_CODE,
      PRJ_IDF: formData.projectId || formData.PRJ_IDF,
      PML_TITLE: formData.title,
      PML_DATE: formData.date,
      PML_STATUS: formData.status,
      PML_DESCRIPTION: formData.description,
    };

    return (
      <GenericPage<any>
        title={isEditing ? t('project.milestones.editTitle', 'Modifier le Jalon') : t('project.milestones.newTitle', 'Nouveau Jalon')}
        icon={<MilestoneIcon />}
        footerEnabled={true}
        footerInfo={formData.last_updt ? { lastUpdate: formData.last_updt, userLib: formData.user_lib } : undefined}
        additionalActions={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={backToList}
          >
            {t('common.backToList', 'Retour à la liste')}
          </Button>
        }
        crudConfig={{ entityKey: 'Blobtic_ProjectManagement,prj_milestone', primaryKey: 'PML_IDF' }}
        data={formPayload}
        onDataChange={handleDataChange}
        onValidate={handleValidate}
        onClear={() => setFormData(emptyMilestone())}
      >
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t('project.milestones.details', 'Détails du jalon')}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={t('project.milestones.project', 'Projet associé *')}
                value={formData.projectId || formData.PRJ_IDF || ''}
                onChange={(e) => setFormData({ ...formData, projectId: Number(e.target.value), PRJ_IDF: Number(e.target.value) })}
                required
              >
                {projectsList.map((p) => (
                  <MenuItem key={p.id || p.PRJ_IDF} value={p.id || p.PRJ_IDF}>
                    {p.title} ({p.code || `PRJ-${p.id}`})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('project.milestones.title', 'Titre du jalon *')}
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label={t('project.milestones.date', 'Date cible *')}
                value={formData.date || ''}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={t('project.milestones.status', 'Statut')}
                value={formData.status || 'PENDING'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as MilestoneStatus })}
              >
                <MenuItem value="PENDING">{t('project.milestones.statusPending', 'En attente')}</MenuItem>
                <MenuItem value="REACHED">{t('project.milestones.statusReached', 'Atteint')}</MenuItem>
                <MenuItem value="DELAYED">{t('project.milestones.statusDelayed', 'En retard')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('project.milestones.description', 'Description du jalon')}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </Paper>
      </GenericPage>
    );
  }

  // Columns for GenericTable
  const columns = [
    {
      id: 'code',
      label: t('project.milestones.code', 'Code'),
      minWidth: 90,
      format: (_: any, row: PrjMilestone) => (
        <Typography variant="body2" fontWeight={600} color="primary">
          {row.code || `PML-${row.id}`}
        </Typography>
      )
    },
    {
      id: 'title',
      label: t('project.milestones.title', 'Jalon'),
      minWidth: 220,
      format: (_: any, row: PrjMilestone) => (
        <Box>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ textDecoration: row.status === 'REACHED' ? 'line-through' : 'none' }}
          >
            {row.title}
          </Typography>
          {row.description && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 280 }}>
              {row.description}
            </Typography>
          )}
        </Box>
      )
    },
    {
      id: 'project',
      label: t('project.milestones.project', 'Projet'),
      minWidth: 180,
      format: (_: any, row: PrjMilestone) => (
        <Typography variant="body2">
          {row.projectTitle || `Projet #${row.projectId}`}
        </Typography>
      )
    },
    {
      id: 'date',
      label: t('project.milestones.date', 'Date cible'),
      minWidth: 120,
      format: (_: any, row: PrjMilestone) => (
        <Typography variant="body2" color="text.secondary">
          {row.date || '-'}
        </Typography>
      )
    },
    {
      id: 'status',
      label: t('project.milestones.status', 'Statut'),
      minWidth: 130,
      format: (_: any, row: PrjMilestone) => (
        <Chip
          label={getStatusLabel(row.status, t)}
          color={getStatusColor(row.status)}
          size="small"
          variant={row.status === 'REACHED' ? 'filled' : 'outlined'}
          sx={{ fontWeight: 600 }}
        />
      )
    }
  ];

  return (
    <GenericPage
      title={t('project.milestones.pageTitle', 'Jalons & Échéances Clés')}
      icon={<MilestoneIcon />}
      footerEnabled={false}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onNew={openNew}
      newButtonLabel={t('project.milestones.new', 'Nouveau Jalon')}
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {viewMode === 'grid' ? (
        <Grid container spacing={2.5}>
          {milestonesList.map((m) => (
            <Grid item xs={12} sm={6} md={4} key={m.id || m.PML_IDF}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                  borderLeft: m.status === 'REACHED' ? '4px solid #4caf50' : m.status === 'DELAYED' ? '4px solid #f44336' : '4px solid #0288d1',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {m.code || `PML-${m.id}`}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{
                          lineHeight: 1.25,
                          mt: 0.25,
                          textDecoration: m.status === 'REACHED' ? 'line-through' : 'none',
                          color: m.status === 'REACHED' ? 'text.secondary' : 'text.primary'
                        }}
                      >
                        {m.title}
                      </Typography>
                    </Box>
                    <Chip
                      label={getStatusLabel(m.status, t)}
                      color={getStatusColor(m.status)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>

                  {m.projectTitle && (
                    <Stack direction="row" spacing={0.8} alignItems="center" mb={1.5}>
                      <ProjectIcon fontSize="small" sx={{ color: 'action.active' }} />
                      <Typography variant="caption" color="primary" fontWeight={600}>
                        {m.projectTitle}
                      </Typography>
                    </Stack>
                  )}

                  {m.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                      {m.description}
                    </Typography>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EventIcon fontSize="small" sx={{ color: 'action.active' }} />
                      <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        {m.date}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={m.status === 'REACHED' ? t('project.milestones.markPending', 'Marquer en attente') : t('project.milestones.markReached', 'Marquer atteint')}>
                        <IconButton size="small" color={m.status === 'REACHED' ? 'success' : 'default'} onClick={() => handleToggleReached(m)}>
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.edit', 'Modifier')}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(m)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete', 'Supprimer')}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(m)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {milestonesList.length === 0 && !loading && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography color="text.secondary">
                  {t('project.milestones.empty', 'Aucun jalon trouvé. Cliquez sur "Nouveau Jalon" pour définir un objectif clé.')}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <GenericTable
          columns={columns}
          data={milestonesList}
          filterColumns={['title', 'projectTitle', 'description', 'code', 'status']}
          searchPlaceholder={t('project.milestones.searchPlaceholder', 'Rechercher un jalon par titre, projet...')}
          maxHeight="calc(100vh - 280px)"
          onEdit={openEdit}
          onDelete={handleDelete}
          rowActions={(row: PrjMilestone) => (
            <Tooltip title={row.status === 'REACHED' ? t('project.milestones.markPending', 'Marquer en attente') : t('project.milestones.markReached', 'Marquer atteint')}>
              <IconButton size="small" color={row.status === 'REACHED' ? 'success' : 'default'} onClick={() => handleToggleReached(row)}>
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        />
      )}
    </GenericPage>
  );
};

export default MilestonesPage;
